import * as React from 'react';

import type {Flow} from 'flow-to-typescript-codemod';
import type {
    ContactUpdateInputFieldInput as ContactUpdateInput,
    SessionUser,
} from 'nutshell-graphql-types';

import {Label as SharedLabel, LoadingState, ErrorState} from '../../peep-details-section';

import {PEEP_ASSIGNEE_SELECT_ID, PeepAssigneeSelect} from '../peep-assignee-select';
import {PEEP_TERRITORY_SELECT_ID, PeepTerritorySelect} from '../peep-territory-select';

import {useGetContactInfo} from './use-get-contact-info';
import {useUpdateContactInfo} from './use-update-contact-info';

type Props = {
    contactId: string;
    isModelLoading: boolean;
    currentUser: SessionUser | null | undefined;
};

export function ContactDetailsSection(props: Props) {
    const {contactId, isModelLoading, currentUser} = props;

    const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

    const {contactInfo, isLoading, errorMessage} = useGetContactInfo(contactId);
    const updateContactInfo = useUpdateContactInfo();

    const handleUpdateContactInfo = (
        updatedInfo: Flow.Diff<
            ContactUpdateInput,
            {
                id: any;
            }
        >
    ) => {
        if (!isUpdating) {
            setIsUpdating(true);

            updateContactInfo({id: contactId, ...updatedInfo}).then(() => {
                setIsUpdating(false);
            });
        }
    };

    if (isModelLoading || isLoading || !currentUser) {
        return <LoadingState />;
    }

    const isDisabled = !contactInfo;

    const Label = (label: {htmlFor: string; children: React.ReactNode}) => (
        <SharedLabel htmlFor={label.htmlFor} isDisabled={isDisabled}>
            {label.children}
        </SharedLabel>
    );

    return (
        <>
            {errorMessage ? <ErrorState /> : undefined}
            <div>
                <Label htmlFor={PEEP_ASSIGNEE_SELECT_ID}>Assignee</Label>
                <PeepAssigneeSelect
                    assigneeId={contactInfo && contactInfo.owner ? contactInfo.owner.id : undefined}
                    onUpdatePeepAssignee={(ownerId?: string | null) => {
                        handleUpdateContactInfo({ownerId});
                    }}
                    isDisabled={isDisabled || !currentUser.permissions.canAssignEntities}
                />
            </div>
            {NutClientConfig.hasTerritories ? (
                <div>
                    <Label htmlFor={PEEP_TERRITORY_SELECT_ID}>Territory</Label>
                    <PeepTerritorySelect
                        territoryId={
                            contactInfo && contactInfo.territory
                                ? contactInfo.territory.id
                                : undefined
                        }
                        onUpdatePeepTerritory={(territoryId?: string | null) => {
                            handleUpdateContactInfo({territoryId});
                        }}
                        isDisabled={isDisabled}
                        canAccessSetup={currentUser.permissions.canAccessSetup}
                    />
                </div>
            ) : undefined}
        </>
    );
}
